// Overview summary cards. Each card sources one backend read and renders its
// own unavailable state, so a credit/benefits outage never blanks payroll data.
// "Next expected pay" is deliberately absent: there is no payroll-calendar API
// yet, and we never fabricate an estimate (design doc §1 gap 2).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, TrendingUp, TrendingDown, Coins, Building2, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { PayslipSummary, YtdSummary } from '../types';
import { formatMoney, formatDate, friendlyStatus, MASK } from '../lib/format';

export interface LoansCardData {
  enabled: boolean; // credit entitlement + employer toggle — card hidden when false
  loading: boolean;
  error: boolean;
  count: number;
  outstanding: number;
  nextDeduction: number; // sum of open-facility installments (same currency only)
  currency: string | null; // null = mixed currencies → no aggregate shown
  canRequest: boolean; // ≥1 enabled offer
}

interface OverviewCardsProps {
  latest?: PayslipSummary;
  prev?: PayslipSummary;
  loading: boolean;
  ytd?: YtdSummary;
  ytdLoading: boolean;
  ytdError: boolean;
  loans: LoansCardData;
  revealed: boolean;
  onToggleReveal: () => void;
  onOpenLoans: () => void;
  onRequestLoan: () => void;
}

export const OverviewCards: React.FC<OverviewCardsProps> = ({
  latest,
  prev,
  loading,
  ytd,
  ytdLoading,
  ytdError,
  loans,
  revealed,
  onToggleReveal,
  onOpenLoans,
  onRequestLoan,
}) => {
  const { t } = useTranslation('payroll');
  const cols = loans.enabled ? 4 : 3;

  if (loading) {
    return (
      <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', cols === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
        {Array.from({ length: cols }, (_, i) => (
          <Card key={i}><CardContent className="p-5"><Skeleton className="h-24 w-full" /></CardContent></Card>
        ))}
      </div>
    );
  }
  if (!latest) return null;

  const money = (n: number, cur: string) => (revealed ? formatMoney(n, cur) : MASK);
  const diffPct = prev && prev.net !== 0 ? ((latest.net - prev.net) / prev.net) * 100 : null;
  const status = friendlyStatus(latest.status);

  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', cols === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
      {/* 1 — Latest net pay */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <CardTitle icon={<Wallet className="h-4 w-4" />} label={t('myPay.lastNetPay')} />
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2" onClick={onToggleReveal}>
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              <span className="text-xs">{revealed ? t('myPay.hide') : t('myPay.show')}</span>
            </Button>
          </div>
          <p className="mt-1 text-h2 tabular-nums text-charcoal">{money(latest.net, latest.currency)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={status.tone as any} className="text-[10px]">{t(status.labelKey)}</Badge>
            <span>{latest.periodLabel}</span>
            <span>· {formatDate(latest.payDate)}</span>
          </div>
          {diffPct != null && revealed && (
            <p
              className={cn(
                'mt-1.5 inline-flex items-center gap-1 text-sm font-medium',
                diffPct < 0 ? 'text-[#991B1B] dark:text-[#fca5a5]' : 'text-[#166534] dark:text-[#86efac]',
              )}
            >
              {diffPct < 0 ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
              <span className="sr-only">{diffPct < 0 ? t('myPay.down') : t('myPay.up')}</span>
              {t('myPay.fromLastPay', { pct: Math.abs(diffPct).toFixed(2) })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* 2 — Outstanding loans & advances (only when the module is on for this tenant/employee) */}
      {loans.enabled && (
        <Card>
          <CardContent className="p-5">
            <CardTitle icon={<Coins className="h-4 w-4" />} label={t('myPay.loansCardTitle')} />
            {loans.loading ? (
              <Skeleton className="mt-2 h-14 w-full" />
            ) : loans.error ? (
              <p className="mt-2 text-sm text-muted-foreground">{t('myPay.sectionUnavailable')}</p>
            ) : loans.count === 0 ? (
              <>
                <p className="mt-2 text-sm text-muted-foreground">{t('myPay.noActiveObligations')}</p>
                {loans.canRequest && (
                  <Button variant="link" size="sm" className="mt-1 h-auto p-0 text-[#D9711E]" onClick={onRequestLoan}>
                    {t('myPay.requestAdvance')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="mt-1 text-h2 tabular-nums text-charcoal">
                  {loans.currency ? money(loans.outstanding, loans.currency) : t('myPay.mixedCurrencies')}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('myPay.activeObligations', { count: loans.count })}
                  {loans.currency && loans.nextDeduction > 0 && (
                    <> · {t('myPay.nextPayrollDeduction', { amount: money(loans.nextDeduction, loans.currency) })}</>
                  )}
                </p>
                <Button variant="link" size="sm" className="mt-1 h-auto p-0 text-[#D9711E]" onClick={onOpenLoans}>
                  {t('myPay.viewRepayments')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3 — This year (net) */}
      <Card>
        <CardContent className="p-5">
          <CardTitle icon={<TrendingUp className="h-4 w-4" />} label={t('myPay.thisYearNet')} />
          {ytdLoading ? (
            <Skeleton className="mt-2 h-14 w-full" />
          ) : ytdError || !ytd ? (
            <p className="mt-2 text-sm text-muted-foreground">{t('myPay.sectionUnavailable')}</p>
          ) : (
            <>
              <p className="mt-1 text-h2 tabular-nums text-charcoal">{money(ytd.net, ytd.currency)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('myPay.ytdOf', { year: ytd.year })}</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* 4 — Total employer value (gross + employer-funded contributions) */}
      <Card>
        <CardContent className="p-5">
          <CardTitle icon={<Building2 className="h-4 w-4" />} label={t('myPay.employerValueTitle')} />
          {ytdLoading ? (
            <Skeleton className="mt-2 h-14 w-full" />
          ) : ytdError || !ytd ? (
            <p className="mt-2 text-sm text-muted-foreground">{t('myPay.sectionUnavailable')}</p>
          ) : (
            <>
              <p className="mt-1 text-h2 tabular-nums text-charcoal">
                {money(ytd.gross + ytd.totalEmployerContributions, ytd.currency)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('myPay.employerValueBreakdown', {
                  gross: money(ytd.gross, ytd.currency),
                  contributions: money(ytd.totalEmployerContributions, ytd.currency),
                })}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">{t('myPay.employerValueNote')}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CardTitle: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
    {icon} {label}
  </div>
);
